/* eslint-disable */
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowRight } from 'lucide-react';
import BaseModal from '@/Components/Modals/BaseModal/BaseModal';
import { Button } from '@/Components/ui/button';
import { modalActions } from '@/Actions';
import { notificationServices } from '@/Services';
import { useAuth } from '@/Components/Auth/Login/auth';
import Stepper from '@/Components/Stepper';
import SelectOrderCategory from './SelectOrderCategory';
import {
	DEFAULT_FLOW_MODE,
	categoryOf,
	getStepConfig,
	resolveFlow,
} from '@/config/createOrdersStepConfig';
import OrderStepFlow from './OrderStepFlow';

const CreateOrdersModal = () => {
	const dispatch = useDispatch();
	const { show, data } = useSelector((state) => state?.modal?.createOrdersModal ?? {});
	const { appConfig = {} } = useAuth();
	const { configs = {} } = appConfig || {};
	const [selectedCategories, setSelectedCategories] = useState([]);
	const [phase, setPhase] = useState('select');
	const [queue, setQueue] = useState([]);
	const [activeIdx, setActiveIdx] = useState(0);
	const [doneKeys, setDoneKeys] = useState([]);
	const savedData = useRef({});
	const modeName = data?.mode || DEFAULT_FLOW_MODE;
	const modeConfig = getStepConfig(modeName) || {};
	const { heading = 'Create Orders' } = modeConfig;
	const modeCategories = modeConfig?.categories || [];
	const showCategoryStep = modeCategories.length > 0;
	const { flatten, merged } = resolveFlow(modeName, selectedCategories);

	useEffect(() => {
		if (show && !showCategoryStep && phase === 'select') {
			savedData.current = data || {};
			setSelectedCategories([]);
			setQueue(resolveFlow(modeName, []).queue);
			setActiveIdx(0);
			setDoneKeys([]);
			setPhase('flow');
		}
	}, [show, showCategoryStep, phase]);

	const resetAll = () => {
		setSelectedCategories([]);
		setPhase('select');
		setQueue([]);
		setActiveIdx(0);
		setDoneKeys([]);
		savedData.current = {};
	};

	const handleClose = () => {
		resetAll();
		dispatch(modalActions.toggleCreateOrdersModal(false));
	};

	const toggleCategory = (key) => {
		setSelectedCategories((prev) => (prev.includes(key)
			? prev.filter((k) => k !== key)
			: [...prev, key]));
	};

	const handleContinue = () => {
		if (!selectedCategories.length) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Please select at least one order category',
			});
			return;
		}
		savedData.current = data || {};
		setQueue(resolveFlow(modeName, selectedCategories).queue);
		setActiveIdx(0);
		setDoneKeys([]);
		setPhase('flow');
	};

	const handleTypeComplete = () => {
		const finishedKey = queue[activeIdx];
		const nextIdx = activeIdx + 1;
		if (nextIdx < queue.length) {
			setDoneKeys((prev) => (prev.includes(finishedKey)
				? prev
				: [...prev, finishedKey]));
			setActiveIdx(nextIdx);
			notificationServices.generateNotification({
				type: 'success',
				message: `${categoryOf(finishedKey)?.label} done — next: ${categoryOf(queue[nextIdx])?.label} (${nextIdx + 1} of ${queue.length})`,
			});
		} else {
			if (queue.length > 1) {
				notificationServices.generateNotification({
					type: 'success',
					message: `All ${queue.length} orders raised`,
				});
			}
			handleClose();
		}
	};

	const renderBtns = () => (
		<div className='flex w-full items-center justify-between'>
			<Button
				variant='ghost'
				className='text-gray-600 hover:bg-gray-100'
				onClick={handleClose}
			>
				Cancel
			</Button>
			<Button
				className='rounded-lg bg-amber-400 px-6 font-semibold text-gray-900 hover:bg-amber-500'
				onClick={handleContinue}
				disabled={!selectedCategories.length}
			>
				{selectedCategories.length > 1
					? `Start ${selectedCategories.length} Orders`
					: 'Continue'}
				<ArrowRight className='ml-1.5 size-4' />
			</Button>
		</div>
	);

	if (!show) return null;

	const activeKey = queue[activeIdx];

	const typeSteps = queue.map((key, idx) => ({
		id: idx + 1,
		title: categoryOf(key)?.label,
		subtitle: doneKeys.includes(key)
			? 'Completed'
			: idx === activeIdx
				? 'In progress'
				: 'Pending',
	}));

	const renderFlowPhase = () => (
		<div className='flex h-full min-h-0 flex-col'>
			{showCategoryStep && (
			<div className='flex shrink-0 items-center gap-x-5 border-b border-gray-100 bg-[#fffdf5] py-2.5 pl-5 pr-14'>
				<div className='shrink-0'>
					<p className='m-0 whitespace-nowrap text-[14px] font-semibold text-gray-900'>
						{flatten
							? 'Create Orders'
							: `Create Orders — ${categoryOf(activeKey)?.label || ''}`}
					</p>
					<p className='m-0 text-[11px] text-gray-500'>
						{flatten
							? `${merged.length} ${merged.length === 1 ? 'category' : 'categories'} in one flow`
							: `Order ${activeIdx + 1} of ${queue.length}`}
					</p>
				</div>
				{flatten && (
					<>
						<span className='h-7 w-px shrink-0 bg-gray-300' />
						<div className='flex min-w-0 flex-1 flex-wrap items-center gap-1.5'>
							{merged.map((key) => {
								const Icon = categoryOf(key)?.icon;
								return (
									<span
										key={key}
										className='flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-100 px-2.5 py-1 text-[11px] font-semibold text-amber-800'
									>
										{Icon && <Icon className='size-3' />}
										{categoryOf(key)?.label || key}
									</span>
								);
							})}
						</div>
					</>
				)}
				{!flatten && queue.length > 1 && (
					<>
						<span className='h-7 w-px shrink-0 bg-gray-300' />
						<div className='min-w-0 flex-1 overflow-x-auto py-0.5'>
							<Stepper
								variant='inline'
								steps={typeSteps}
								currentStep={activeIdx + 1}
								classes={{
									container: 'flex w-max items-center',
									circleBase: 'w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-semibold flex-shrink-0 transition-all duration-300',
									labelWrap: 'flex flex-col items-start leading-tight ml-2',
									title: 'text-[12px] font-semibold whitespace-nowrap',
									subtitle: 'text-[10px] text-gray-400 font-normal',
									connector: 'w-6 h-[2px] mx-3 bg-gray-200 transition-all duration-300',
								}}
							/>
						</div>
					</>
				)}
			</div>
			)}
			<div className='min-h-0 flex-1'>
				<OrderStepFlow
					key={`${activeIdx}-${activeKey}`}
					modeName={modeName}
					category={categoryOf(activeKey)}
					selectedCategoryKeys={merged}
					navSelectedStore={savedData.current?.navSelectedStore || {}}
					onComplete={handleTypeComplete}
					onBackToCategories={activeIdx === 0 && showCategoryStep
						? () => setPhase('select')
						: null}
				/>
			</div>
		</div>
	);

	return (
		<BaseModal
			open={show}
			heading={heading}
			handleClose={handleClose}
			{...(phase === 'select' ? { renderBtns } : { hideFooter: true })}
			{...(phase === 'flow' && {
				wrapperClassName: '!max-h-full !p-0 !text-left',
			})}
			width='100%'
			height='100%'
			customStyles={{
				maxWidth: '100vw',
				maxHeight: '100vh',
				borderRadius: 0,
			}}
		>
			{phase === 'select' ? (
				<SelectOrderCategory
					selectedCategories={selectedCategories}
					onToggle={toggleCategory}
					internalPOTypes={configs?.internalPOTypes}
					flatten={flatten}
					categories={modeCategories}
				/>
			) : renderFlowPhase()}
		</BaseModal>
	);
};

export default CreateOrdersModal;
